import { learningApi } from '../../../../services/api'
import useSWR, { SWRConfiguration } from 'swr'
import { IMenuResponse } from './types'
import { toast } from 'react-toastify'
import { produce } from 'immer'

interface IParams {
  moduleId: string
  courseId: string
}

export function useGetMenuContent({ moduleId, courseId }: IParams) {
  const token = localStorage.getItem('token')

  const key = token ? [`/content/menu/${moduleId}?courseId=${courseId}`, token] : null

  const swr = useSWR<IMenuResponse>(key, fetcher, configuration)

  const completeChapter = (params: { lessonId: string; chapterId: string; isCompleted: boolean }) =>
    swr.mutate(
      (data) =>
        produce(data, (draft) => {
          const lesson = draft.children.find((lesson) => lesson._id === params.lessonId)

          const chapter = lesson?.children.find((chapter) => chapter._id === params.chapterId)

          if (!chapter) return

          chapter.progress = params.isCompleted ? 100 : 0

          const isLessonCompleted = lesson.children.every((lesson) => lesson.progress > 95)

          lesson.completed = isLessonCompleted
        }),
      {
        revalidate: false,
      }
    )

  return { ...swr, completeChapter }
}

const fetcher = ([url, token]: [url: string, token: string]) =>
  learningApi
    .get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      let totalNonEmptyLessons = 0

      const lessons = res.data.children.map((lesson, index) => {
        const isNotEmpty = lesson.children.length > 0

        if (isNotEmpty) totalNonEmptyLessons++

        const chapters = lesson.children.map((children, index) => ({
          ...children,
          position: index + 1,
        }))

        return {
          ...lesson,
          position: index + 1,
          children: chapters,
        }
      })

      return {
        ...res.data,
        children: lessons,
        totalNonEmptyLessons,
      }
    })

const configuration: SWRConfiguration = {
  shouldRetryOnError: true,
  onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
    if (retryCount > 3) {
      if (error?.response?.status === 401) return
      toast.error(
        'Não foi possível carregar os dados do menu. Por favor, tente novamente mais tarde'
      )
    } else {
      setTimeout(() => revalidate({ retryCount }), 500)
    }
  },
}

export type TSwrMenuContent = ReturnType<typeof useGetMenuContent>
